import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { AuthLayout } from "../../components/AppShell";
import { Alert, Button, Field, Input, Spinner } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { PasswordRules } from "./PasswordPages";

/** The token travels in the URL fragment, so it never reaches server logs or Referer headers. */
function tokenFromHash() {
  return new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "";
}

export function AcceptInvitePage() {
  const [token] = useState(tokenFromHash);
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const invite = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api.post<{ companyName: string; email: string; needsPassword: boolean }>("/api/v1/invitations/describe", { token }),
    enabled: Boolean(token),
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () => api.post("/api/v1/invitations/accept", invite.data?.needsPassword ? { token, password } : { token }),
    onSuccess: () => {
      history.replaceState(null, "", window.location.pathname);
      navigate("/login?invited=1", { replace: true });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (invite.data?.needsPassword && password !== confirm) return setLocalError("The passwords don't match.");
    setLocalError(null);
    accept.mutate();
  }

  if (!token || invite.isError) {
    return (
      <AuthLayout title="Invitation unavailable">
        <Alert tone="danger">{invite.isError ? errorMessage(invite.error) : "This invitation link is incomplete."}</Alert>
        <Link to="/login" className="mt-5 block text-center text-sm font-medium text-accent hover:underline">
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }
  if (invite.isLoading || !invite.data)
    return (
      <AuthLayout title="Accept invitation">
        <Spinner label="Checking invitation" />
      </AuthLayout>
    );

  return (
    <AuthLayout
      title="Accept your invitation"
      subtitle={
        <>
          You've been invited to <strong className="text-fg">{invite.data.companyName}</strong> on Galeway.
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        {(localError || accept.isError) && <Alert tone="danger">{localError ?? errorMessage(accept.error)}</Alert>}
        <Field label="Email">{(p) => <Input {...p} value={invite.data!.email} readOnly className="h-11 bg-surface-2" />}</Field>
        {invite.data.needsPassword ? (
          <>
            <Field label="Create a password">
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                />
              )}
            </Field>
            <PasswordRules password={password} />
            <Field label="Confirm password">
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-11"
                />
              )}
            </Field>
          </>
        ) : (
          <Alert>You already have a Galeway login. Accept to add {invite.data.companyName} to your account, then sign in as usual.</Alert>
        )}
        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={accept.isPending}
          disabled={invite.data.needsPassword && (password.length < 12 || !confirm)}
        >
          Accept invitation
        </Button>
      </form>
    </AuthLayout>
  );
}
